import { el } from 'redom';
import cloneDeep from 'lodash/cloneDeep';

import { ContentsManager } from '../Content/contentsManager';
import { ContentModel } from '../Content/contentModel';
import { IVariantText } from '../Content/Pattern/Decal/patternText';
import { ConfigurationTranslation } from './configurationTranslation';
import { defaultViews, ToolScreenshot } from '../Tool/toolScreenshot';
import { SystemLayer } from '../System/systemLayer';
import { IArcCameraAngles } from '../System/systemCamera';

export class ConfigurationTechnicalFile {
    private system: SystemLayer;

    private configurationTranslation: ConfigurationTranslation

    private contentsManager: ContentsManager

    private toolScreenshot: ToolScreenshot

    // IMAGES
    private imageFront: HTMLImageElement

    private imageLeft: HTMLImageElement

    private imageBack: HTMLImageElement

    // CANVAS
    private canvas: HTMLCanvasElement;

    private ctx: CanvasRenderingContext2D;

    constructor(
        system: SystemLayer,
        configurationTranslation: ConfigurationTranslation,
        toolScreenshot: ToolScreenshot,
        contentsManager: ContentsManager
    ) {
        this.system = system;
        this.configurationTranslation = configurationTranslation;
        this.toolScreenshot = toolScreenshot;
        this.contentsManager = contentsManager;

        this.canvas = document.createElement('canvas');
        this.canvas.setAttribute('width', "2000px")
        this.canvas.setAttribute('height', "700px")
        this.ctx = this.canvas.getContext('2d');
        //* For test purpose
        // document.body.appendChild(this.canvas)

        this.imageFront = el('img', { crossOrigin: 'anonymous' }) as HTMLImageElement;
        this.imageLeft = el('img', { crossOrigin: 'anonymous' }) as HTMLImageElement;
        this.imageBack = el('img', { crossOrigin: 'anonymous' }) as HTMLImageElement;
    }

    private langages = ['en']

    public setLangages(langages: string[]): void {
        this.langages = langages;
    }

    public getProductImagesAndVariants(callback: (image: string) => void): void {
        this.setVariants();
        this.setImages(() => {
            this.getJpeg(callback);
        });
    }

    private getJpeg(callback: (image: string) => void) {
        // Iphone Safari only fix is to repeat the action
        this.validateImage(5, () => {
            var dataURL = this.canvas.toDataURL();
            callback(dataURL)
        });
    }

    private currentLoop = 0

    private validateImage(times, callback: () => void) {
        this.currentLoop = 0;
        this.loopValidation(times, () => {
            callback();
        });
    }

    private loopValidation(times, callback: () => void) {
        this.currentLoop++;
        this.getPixelImage((hasColor) => {
            console.log(hasColor);

            if (hasColor) {
                callback();
            } else if (this.currentLoop === times) {
                if (!hasColor) console.error(`Naker: Fail to screenshot technical file after ${times} attempt`);
                callback();
            } else {
                // Try again
                this.setImages(() => {
                    this.loopValidation(times, callback);
                });
            }
        });
    }

    private getPixelImage(callback: (hasColor: boolean) => void) {
        const { width, height } = this.imageSize
        const imageData = this.ctx.getImageData(0, 0, width, height);

        // Now you can access pixel data from imageData.data.
        // It's a one-dimensional array of RGBA values.
        // Match with technical second image
        // Check Sass file to confirm
        const x = width / 2;
        const y = height / 2;
        const index = (y * imageData.width + x) * 4;
        const red = imageData.data[index];
        const green = imageData.data[index + 1];
        const blue = imageData.data[index + 2];
        // const alpha = imageData.data[index + 3];
        if (red !== 255 || green !== 255 || blue !== 255) callback(true);
        else callback(false);
    }

    private views = cloneDeep(defaultViews);

    public setViews(views: IArcCameraAngles[]): void {
        this.views = views;
    }

    private imageLoaded = 0

    private imageSize = { width: 400, height: 700 };

    private setImages(callback: () => void) {
        const previusSize = this.toolScreenshot.getSize();
        this.toolScreenshot.setSize(this.imageSize);
        this.toolScreenshot.shotViews(this.views, (views) => {
            this.toolScreenshot.setSize(previusSize);
            this.imageLoaded = 0;
            [this.imageFront.src, this.imageLeft.src, this.imageBack.src] = views;
            //! Need to make sure images are loaded or they won't be on the technical file
            this.imageFront.onload = () => {
                this.ctx.drawImage(this.imageFront, 0, 0, 400, 700);
                this.testImageLoad(callback);
            };
            this.imageLeft.onload = () => {
                this.ctx.drawImage(this.imageLeft, 400, 0, 400, 700);
                this.testImageLoad(callback);
            };
            this.imageBack.onload = () => {
                this.ctx.drawImage(this.imageBack, 800, 0, 400, 700);
                this.testImageLoad(callback);
            };
            // Manage error too to make sure we have a callback
            this.imageFront.onerror = (e) => { this.testImageLoad(callback, e); };
            this.imageLeft.onerror = (e) => { this.testImageLoad(callback, e); };
            this.imageBack.onerror = (e) => { this.testImageLoad(callback, e); };
        });
    }

    testImageLoad(callback: () => void, error?: string | Event): void {
        if (error) console.error('Naker: Fail to add technical file image', error);
        this.imageLoaded++;
        if (this.imageLoaded === 3) {
            // On some devices like mobiles rendering images can take time
            callback();
        }
    }

    private setVariants() {
        const model = this.getModel();
        const variantMeshesMaterialsList = model.getVariantMeshesMaterialsList();
        const LEFT = 1200;
        const WIDTH = 250;
        const HEIGHT = 70;
        let i = 0, j = 0;
        variantMeshesMaterialsList.map((v) => {
            // POSITION
            const left = LEFT + j * WIDTH
            const top = i * HEIGHT

            // TITLE BACKGROUND
            this.ctx.fillStyle = '#AAA';
            this.ctx.fillRect(left, top, WIDTH - 10, 25);

            const { meshes, decalGeometry } = v;
            // TITLE MESH NAME
            const translatedName = this.getMeshesName(meshes);
            this.printAt(translatedName, left + 5, top + 18, WIDTH - 10)

            // MATERIAL COLOR AND NAME
            const { color, materialName } = this.getMeshesMaterial(meshes);
            this.drawMaterial(left, top + 30, color);

            this.printAt(materialName, left + 25, top + 45, WIDTH - 50)

            // TEXT CUSTOM
            if (decalGeometry) {
                const textLabel = this.getMeshesDecal(meshes);
                if (textLabel) {
                    this.printAt(textLabel, left + 5, top + 70, WIDTH - 10)
                    i++;
                }
            }

            i++;
            if (i > 8) {
                j++;
                i = 0;
            }
        });
    }

    private drawMaterial(xPos: number, yPos: number, color: string) {
        const size = 20;
        const thickness = 1;
        this.ctx.fillStyle = 'grey';
        this.ctx.fillRect(xPos, yPos, size, size);

        this.ctx.fillStyle = color;
        this.ctx.fillRect(xPos + thickness, yPos + thickness, size - (thickness * 2), size - (thickness * 2));
    }

    private printAt(text, x, y, fitWidth) {
        this.ctx.fillStyle = 'black';
        this.ctx.font = "13px serif";
        fitWidth = fitWidth || 0;
        const lineHeight = 20;
        if (fitWidth <= 0) {
            this.ctx.fillText(text, x, y);
            return;
        }
        for (var idx = 1; idx <= text.length; idx++) {
            var str = text.substr(0, idx);
            if (this.ctx.measureText(str).width > fitWidth) {
                this.ctx.fillText(text.substr(0, idx - 1), x, y);
                this.printAt(text.substr(idx - 1), x, y + lineHeight, fitWidth);
                return;
            }
        }
        this.ctx.fillText(text, x, y);
    }

    private getModel(): ContentModel {
        return this.contentsManager.getFirstModel();
    }

    private getMeshesName(meshes: string[]): string {
        const model = this.getModel();
        const variantMeshes = model.getVariantMeshesPropertiesByMeshes(meshes);
        if (variantMeshes && variantMeshes.nakerName) {
            return this.getTranslations(variantMeshes.nakerName);
        } if (variantMeshes && variantMeshes.meshes) {
            return this.getTranslations(variantMeshes.meshes[0]);
        }
        return '';
    }

    private getMeshesMaterial(meshes: string[]): { color: string, materialName: string } {
        const model = this.getModel();
        const selected = model.getSelectedVariantMeshes(meshes);
        let color = ''; let materialName = 'not selected';
        if (selected && selected.material) {
            const toolM = this.system.toolMaterial;
            color = toolM.getColorFromMaterialKey(selected.material, true, false);
            const material = this.system.toolMaterial.getMaterialByKey(selected.material);
            materialName = material.name;
            materialName = this.getTranslations(materialName)
        }
        return { color, materialName };
    }

    private getMeshesDecal(meshes: string[]): string {
        const model = this.getModel();
        const patternText = model.getPatternText(meshes);
        const variantText = (patternText) ? patternText.getVariant() as IVariantText : null;
        if (variantText && variantText.text) {
            return `"${variantText.text}" (${variantText.font})`;
        }
        return '';
    }

    private getTranslations(key: string): string {
        const translations = [];
        this.langages.map((langage) => {
            const trans = this.configurationTranslation.getLangageString(langage, key);
            if (!translations.includes(trans)) translations.push(trans);
            return langage;
        });
        const translationsString = translations.join(' - ');
        return translationsString;
    }
}
